﻿import Dexie from 'dexie'
import { db, ensureInitialCategory } from '../db/catalogDb'
import type {
  CategoryRecord,
  Id,
  ImageDraft,
  ImageMetaRecord,
  ImagePageQuery,
  ImagePageResult,
  ImageSearchRecord,
  ImportOptions,
  ImportProgress,
  StorageInfo
} from '../types/catalog'
import { buildImageTokens, fileExtension, fileStem, normalizeTags, tokenize } from '../utils/text'
import { processImage, rotateImage as rotateProcessedImage } from './imageProcessing'

function categoryContains(categoryId: Id | null, includeDescendants: boolean, image: ImageMetaRecord): boolean {
  if (!categoryId) return true
  return includeDescendants
    ? image.categoryAncestors.includes(categoryId)
    : image.categoryId === categoryId
}

function buildSearchRows(image: ImageMetaRecord): ImageSearchRecord[] {
  const prefixes = new Set<string>()
  for (const token of image.tokens) {
    const maxLength = Math.min(token.length, 32)
    for (let length = 2; length <= maxLength; length += 1) prefixes.add(token.slice(0, length))
  }

  return [...prefixes].map((token) => ({
    key: `${token}\u0000${image.id}`,
    token,
    imageId: image.id,
    categoryId: image.categoryId,
    categoryAncestors: image.categoryAncestors,
    updatedAt: image.updatedAt
  }))
}

async function getCategoryAncestors(categoryId: Id): Promise<Id[]> {
  const category = await db.categories.get(categoryId)
  if (!category) throw new Error('Category not found')
  return [...category.ancestorIds, category.id]
}

export async function initializeCatalog(): Promise<CategoryRecord> {
  await db.open()
  return ensureInitialCategory()
}

export async function listCategories(): Promise<CategoryRecord[]> {
  return db.categories.orderBy('name').toArray()
}

export async function createCategory(name: string, parentId: Id | null): Promise<CategoryRecord> {
  const cleanName = name.trim().replace(/\s+/g, ' ')
  if (!cleanName) throw new Error('Enter a category name')

  const parent = parentId ? await db.categories.get(parentId) : null
  if (parentId && !parent) throw new Error('Parent category not found')

  const duplicate = await db.categories
    .filter((candidate) => candidate.parentId === parentId && candidate.name === cleanName)
    .first()
  if (duplicate) throw new Error('A category with this name already exists at the same level')

  const now = Date.now()
  const record: CategoryRecord = {
    id: crypto.randomUUID(),
    name: cleanName,
    parentId,
    ancestorIds: parent ? [...parent.ancestorIds, parent.id] : [],
    depth: parent ? parent.depth + 1 : 0,
    directImageCount: 0,
    createdAt: now,
    updatedAt: now
  }
  await db.categories.add(record)
  return record
}

export async function renameCategory(categoryId: Id, name: string): Promise<void> {
  const category = await db.categories.get(categoryId)
  if (!category) throw new Error('Category not found')
  const cleanName = name.trim().replace(/\s+/g, ' ')
  if (!cleanName) throw new Error('Enter a category name')

  const duplicate = await db.categories
    .filter((candidate) => candidate.parentId === category.parentId && candidate.name === cleanName && candidate.id !== categoryId)
    .first()
  if (duplicate) throw new Error('A category with this name already exists at the same level')

  await db.categories.update(categoryId, { name: cleanName, updatedAt: Date.now() })
}

export async function deleteCategory(categoryId: Id): Promise<void> {
  const category = await db.categories.get(categoryId)
  if (!category) return
  if (category.directImageCount > 0) throw new Error('Move or delete the images in this category first')
  const child = await db.categories.where('parentId').equals(categoryId).first()
  if (child) throw new Error('Delete subcategories first')
  await db.categories.delete(categoryId)
}

async function insertProcessedImage(
  file: File,
  options: ImportOptions,
  capturedAt?: number
): Promise<ImageMetaRecord> {
  const processed = await processImage(file)
  const id = crypto.randomUUID()
  const now = Date.now()
  const tags = normalizeTags(options.tags)
  const title = fileStem(file.name)
  const categoryAncestors = await getCategoryAncestors(options.categoryId)
  const image: ImageMetaRecord = {
    id,
    categoryId: options.categoryId,
    categoryAncestors,
    title,
    originalName: file.name,
    mimeType: processed.mimeType,
    extension: fileExtension(file.name, processed.mimeType),
    size: processed.size,
    width: processed.width,
    height: processed.height,
    description: options.description.trim(),
    tags,
    tokens: buildImageTokens(title, options.description, tags),
    createdAt: now,
    updatedAt: now,
    capturedAt
  }

  await db.transaction('rw', db.images, db.imageFiles, db.imageSearch, db.categories, async () => {
    await db.images.add(image)
    await db.imageFiles.add({
      id,
      original: processed.original,
      preview: processed.preview,
      thumbnail: processed.thumbnail
    })
    const searchRows = buildSearchRows(image)
    if (searchRows.length) await db.imageSearch.bulkAdd(searchRows)
    await db.categories.where('id').equals(options.categoryId).modify((category) => {
      category.directImageCount += 1
      category.updatedAt = now
    })
  })

  return image
}

export async function importImages(
  files: File[],
  options: ImportOptions,
  onProgress?: (progress: ImportProgress) => void,
  capturedAt?: number
): Promise<{ imported: number; failed: Array<{ file: string; error: string }> }> {
  if (!files.length) return { imported: 0, failed: [] }
  if (!await db.categories.get(options.categoryId)) throw new Error('Select a valid category')

  let imported = 0
  const failed: Array<{ file: string; error: string }> = []

  for (let index = 0; index < files.length; index += 1) {
    const file = files[index]
    onProgress?.({ current: index + 1, total: files.length, fileName: file.name, imported, failed: failed.length })
    try {
      await insertProcessedImage(file, options, capturedAt)
      imported += 1
    } catch (error) {
      failed.push({
        file: file.name,
        error: error instanceof Error ? error.message : 'Import failed'
      })
    }
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()))
  }

  onProgress?.({ current: files.length, total: files.length, fileName: '', imported, failed: failed.length })
  if (imported > 0 && navigator.storage?.persist) void navigator.storage.persist()
  return { imported, failed }
}

function allTokensMatch(image: ImageMetaRecord, queryTokens: string[]): boolean {
  return queryTokens.every((query) => image.tokens.some((token) => token.startsWith(query)))
}

export async function getImagePage(query: ImagePageQuery): Promise<ImagePageResult> {
  const queryTokens = tokenize(query.search)

  if (!queryTokens.length) {
    let collection
    if (!query.categoryId) {
      collection = db.images.orderBy('[updatedAt+id]').reverse()
    } else if (query.includeDescendants) {
      collection = db.images
        .orderBy('[updatedAt+id]')
        .reverse()
        .filter((image) => image.categoryAncestors.includes(query.categoryId as Id))
    } else {
      collection = db.images
        .where('[categoryId+updatedAt+id]')
        .between(
          [query.categoryId, Dexie.minKey, Dexie.minKey],
          [query.categoryId, Dexie.maxKey, Dexie.maxKey],
          true,
          true
        )
        .reverse()
    }

    const items = await collection.offset(query.offset).limit(query.pageSize + 1).toArray()
    const hasMore = items.length > query.pageSize
    if (hasMore) items.pop()
    return {
      items,
      nextOffset: query.offset + items.length,
      nextScanOffset: 0,
      hasMore
    }
  }

  const firstToken = queryTokens[0].slice(0, 32)
  const page: ImageMetaRecord[] = []
  let scanOffset = query.scanOffset
  let exhausted = false
  const batchSize = Math.max(query.pageSize * 5, 100)

  while (page.length < query.pageSize && !exhausted) {
    let searchCollection
    if (query.categoryId && !query.includeDescendants) {
      searchCollection = db.imageSearch
        .where('[token+categoryId+updatedAt+imageId]')
        .between(
          [firstToken, query.categoryId, Dexie.minKey, Dexie.minKey],
          [firstToken, query.categoryId, Dexie.maxKey, Dexie.maxKey],
          true,
          true
        )
        .reverse()
    } else {
      searchCollection = db.imageSearch
        .where('[token+updatedAt+imageId]')
        .between(
          [firstToken, Dexie.minKey, Dexie.minKey],
          [firstToken, Dexie.maxKey, Dexie.maxKey],
          true,
          true
        )
        .reverse()
    }

    const rows = await searchCollection.offset(scanOffset).limit(batchSize).toArray()
    scanOffset += rows.length
    exhausted = rows.length < batchSize
    if (!rows.length) break

    const records = await db.images.bulkGet(rows.map((row) => row.imageId))
    for (const image of records) {
      if (!image) continue
      if (!categoryContains(query.categoryId, query.includeDescendants, image)) continue
      if (!allTokensMatch(image, queryTokens)) continue
      page.push(image)
      if (page.length >= query.pageSize) break
    }
  }

  return {
    items: page,
    nextOffset: query.offset + page.length,
    nextScanOffset: scanOffset,
    hasMore: !exhausted
  }
}

export async function getImage(id: Id): Promise<ImageMetaRecord | undefined> {
  return db.images.get(id)
}

export async function getImageBlob(id: Id, variant: 'thumbnail' | 'preview' | 'original'): Promise<Blob | undefined> {
  const record = await db.imageFiles.get(id)
  return record?.[variant]
}

export async function updateImage(id: Id, draft: ImageDraft): Promise<ImageMetaRecord> {
  const current = await db.images.get(id)
  if (!current) throw new Error('Image not found')
  const category = await db.categories.get(draft.categoryId)
  if (!category) throw new Error('Category not found')

  const now = Date.now()
  const tags = normalizeTags(draft.tags)
  const updated: ImageMetaRecord = {
    ...current,
    title: draft.title.trim() || fileStem(current.originalName),
    categoryId: category.id,
    categoryAncestors: [...category.ancestorIds, category.id],
    description: draft.description.trim(),
    tags,
    updatedAt: now,
    tokens: []
  }
  updated.tokens = buildImageTokens(updated.title, updated.description, updated.tags)

  await db.transaction('rw', db.images, db.imageSearch, db.categories, async () => {
    await db.images.put(updated)
    await db.imageSearch.where('imageId').equals(id).delete()
    const rows = buildSearchRows(updated)
    if (rows.length) await db.imageSearch.bulkAdd(rows)

    if (current.categoryId !== updated.categoryId) {
      await db.categories.where('id').equals(current.categoryId).modify((record) => {
        record.directImageCount = Math.max(0, record.directImageCount - 1)
        record.updatedAt = now
      })
      await db.categories.where('id').equals(updated.categoryId).modify((record) => {
        record.directImageCount += 1
        record.updatedAt = now
      })
    }
  })

  return updated
}

export async function rotateImage(id: Id, degrees: 90 | -90 | 180): Promise<ImageMetaRecord> {
  const [meta, binary] = await Promise.all([db.images.get(id), db.imageFiles.get(id)])
  if (!meta || !binary) throw new Error('Image not found')
  if (!['image/jpeg', 'image/png', 'image/webp'].includes(meta.mimeType)) {
    throw new Error('Rotation is available for JPEG, PNG and WebP')
  }

  const processed = await rotateProcessedImage(binary.original, degrees, meta.mimeType)
  const updated: ImageMetaRecord = {
    ...meta,
    mimeType: processed.mimeType,
    size: processed.size,
    width: processed.width,
    height: processed.height,
    updatedAt: Date.now()
  }

  await db.transaction('rw', db.images, db.imageFiles, db.imageSearch, async () => {
    await db.images.put(updated)
    await db.imageFiles.put({ id, original: processed.original, preview: processed.preview, thumbnail: processed.thumbnail })
    await db.imageSearch.where('imageId').equals(id).delete()
    const rows = buildSearchRows(updated)
    if (rows.length) await db.imageSearch.bulkAdd(rows)
  })

  return updated
}

export async function deleteImage(id: Id): Promise<void> {
  const image = await db.images.get(id)
  if (!image) return
  await db.transaction('rw', db.images, db.imageFiles, db.imageSearch, db.categories, async () => {
    await Promise.all([
      db.images.delete(id),
      db.imageFiles.delete(id),
      db.imageSearch.where('imageId').equals(id).delete()
    ])
    await db.categories.where('id').equals(image.categoryId).modify((category) => {
      category.directImageCount = Math.max(0, category.directImageCount - 1)
      category.updatedAt = Date.now()
    })
  })
}

export async function countImages(): Promise<number> {
  return db.images.count()
}

export async function getStorageInfo(): Promise<StorageInfo> {
  const estimate = await navigator.storage?.estimate?.()
  const persisted = await navigator.storage?.persisted?.() ?? false
  return {
    usage: estimate?.usage ?? 0,
    quota: estimate?.quota ?? 0,
    persisted
  }
}
